import React, { useState, useEffect, useRef } from 'react';
import { X, ChevronLeft, ChevronRight, Heart, Send } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { Button } from './ui/button';
import { Progress } from './ui/progress';

const StoryViewer = ({ entity, stories, onClose, onProfileClick, onStoryViewed }) => {
  const [currentCardIndex, setCurrentCardIndex] = useState(0);
  const [progress, setProgress] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const [quizAnswers, setQuizAnswers] = useState({}); // Track quiz answers
  const viewerRef = useRef(null);

  const currentStory = stories[0]; // For now, show first story
  const currentCard = currentStory?.cards[currentCardIndex];
  const totalCards = currentStory?.cards.length || 0;

  // Mark story as viewed when opened
  useEffect(() => {
    if (currentStory && onStoryViewed) {
      onStoryViewed(currentStory.id);
    }
  }, [currentStory, onStoryViewed]);

  // Update progress manually when advancing slides
  useEffect(() => {
    setProgress(((currentCardIndex + 1) / totalCards) * 100);
  }, [currentCardIndex, totalCards]);

  // Handle manual navigation - no autoplay
  const handleNext = () => {
    if (currentCardIndex < totalCards - 1) {
      setCurrentCardIndex(prev => prev + 1);
      setProgress(((currentCardIndex + 2) / totalCards) * 100);
    } else {
      onClose();
    }
  };

  const handlePrevious = () => {
    if (currentCardIndex > 0) {
      setCurrentCardIndex(prev => prev - 1);
      setProgress(((currentCardIndex) / totalCards) * 100);
    }
  };

  // Social media-like tap navigation
  const handleTap = (e) => {
    const rect = viewerRef.current.getBoundingClientRect();
    const tapX = e.clientX - rect.left;
    const screenWidth = rect.width;
    
    if (tapX < screenWidth / 2) {
      // Tapped left side - go to previous
      handlePrevious();
    } else {
      // Tapped right side - go to next
      handleNext();
    }
  };

  // Touch handling for mobile
  const handleTouchStart = (e) => {
    setTouchEnd(null);
    const touch = e.targetTouches[0];
    setTouchStart({
      x: touch.clientX,
      y: touch.clientY
    });
  };

  const handleTouchMove = (e) => {
    const touch = e.targetTouches[0];
    setTouchEnd({
      x: touch.clientX,
      y: touch.clientY
    });
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distanceX = touchStart.x - touchEnd.x;
    const distanceY = touchStart.y - touchEnd.y;
    
    const isHorizontalSwipe = Math.abs(distanceX) > Math.abs(distanceY);
    const isVerticalSwipe = Math.abs(distanceY) > Math.abs(distanceX);
    
    if (isHorizontalSwipe) {
      // Horizontal swipe - navigate stories
      const isLeftSwipe = distanceX > 50;
      const isRightSwipe = distanceX < -50;
      
      if (isLeftSwipe) {
        handleNext();
      } else if (isRightSwipe) {
        handlePrevious();
      }
    } else if (isVerticalSwipe) {
      // Vertical swipe - close if swiping down
      const isDownSwipe = distanceY < -100; // Swipe down threshold
      
      if (isDownSwipe) {
        onClose();
      }
    }
  };

  // Handle swipe down to close (check for vertical swipe)
  const handleVerticalSwipe = (e) => {
    const touch = e.changedTouches[0];
    const swipeDistance = touch.clientY - (touchStart?.y || 0);
    
    if (swipeDistance > 100) { // Swipe down threshold
      onClose();
    }
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (e.key === 'ArrowLeft') handlePrevious();
      if (e.key === 'ArrowRight') handleNext();
      if (e.key === 'Escape') onClose();
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [currentCardIndex]);

  if (!currentCard) return null;

  return (
    <div 
      ref={viewerRef}
      className="fixed inset-0 bg-black z-50 flex items-center justify-center"
      onClick={handleTap}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      <div className="relative w-full max-w-md h-full bg-black">
        {/* Progress bars at top */}
        <div className="absolute top-2 left-4 right-4 z-10 flex gap-1">
          {currentStory?.cards.map((_, index) => (
            <div key={index} className="flex-1 h-1 bg-white bg-opacity-30 rounded-full overflow-hidden">
              <div 
                className="h-full bg-white transition-all duration-200"
                style={{ 
                  width: index < currentCardIndex ? '100%' : 
                         index === currentCardIndex ? '100%' : '0%' // Show full bar for current slide
                }}
              />
            </div>
          ))}
        </div>

        {/* Header */}
        <div className="absolute top-6 left-4 right-4 z-10 flex items-center justify-between">
          <div 
            className="flex items-center gap-2 cursor-pointer hover:opacity-80"
            onClick={(e) => {
              e.stopPropagation(); // Prevent tap navigation
              if (onProfileClick) {
                onProfileClick(entity);
                onClose(); // Close story viewer when opening profile
              }
            }}
          >
            <Avatar className="w-8 h-8">
              <AvatarImage src={entity.avatar_url} alt={entity.name} />
              <AvatarFallback>{entity.name.charAt(0)}</AvatarFallback>
            </Avatar>
            <span className="text-white text-sm font-medium">{entity.handle}</span>
            <span className="text-white text-opacity-60 text-xs">
              {Math.floor((Date.now() - new Date(currentCard.created_at)) / (1000 * 60 * 60))}h
            </span>
          </div>
          
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={(e) => {
              e.stopPropagation();
              onClose();
            }}
            className="text-white hover:bg-white hover:bg-opacity-20"
          >
            <X className="w-5 h-5" />
          </Button>
        </div>

        {/* Story Content */}
        <div className="w-full h-full flex items-center justify-center px-4">
          <div className="text-center text-white max-w-sm">
            {currentCard.media_url && (
              <div className="mb-6">
                <img 
                  src={currentCard.media_url} 
                  alt="Story media" 
                  className="w-full max-h-96 object-cover rounded-lg"
                />
              </div>
            )}
            
            <h2 className="text-xl font-bold mb-4">{currentCard.title}</h2>
            <p className="text-lg mb-6 leading-relaxed">{currentCard.content}</p>
            
            {currentCard.quiz && (
              <div className="bg-black bg-opacity-50 rounded-lg p-4 mb-4">
                <h3 className="font-semibold mb-3">💡 Quick Quiz</h3>
                <p className="text-sm mb-3">{currentCard.quiz.question}</p>
                <div className="space-y-2">
                  {currentCard.quiz.options.map((option, index) => {
                    const isSelected = quizAnswers[currentCard.id] === index;
                    const isAnswered = quizAnswers[currentCard.id] !== undefined;
                    const isCorrect = index === currentCard.quiz.correct_answer;
                    
                    return (
                      <button
                        key={index}
                        className={`w-full text-left p-2 rounded text-sm transition-all ${
                          isAnswered
                            ? isCorrect
                              ? 'bg-green-500 bg-opacity-80 text-white'
                              : isSelected && !isCorrect
                                ? 'bg-red-500 bg-opacity-80 text-white'
                                : 'bg-white bg-opacity-20 text-white opacity-60'
                            : 'bg-white bg-opacity-20 hover:bg-opacity-30 text-white'
                        }`}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (!isAnswered) {
                            setQuizAnswers(prev => ({
                              ...prev,
                              [currentCard.id]: index
                            }));
                          }
                        }}
                        disabled={isAnswered}
                      >
                        {String.fromCharCode(65 + index)}. {option}
                        {isAnswered && isCorrect && (
                          <span className="ml-2">✅</span>
                        )}
                      </button>
                    );
                  })}
                </div>
                
                {/* Show explanation after answering */}
                {quizAnswers[currentCard.id] !== undefined && currentCard.quiz.explanation && (
                  <div className="mt-3 p-2 bg-blue-500 bg-opacity-30 rounded text-xs">
                    <p className="font-semibold">
                      {quizAnswers[currentCard.id] === currentCard.quiz.correct_answer ? '✅ Correct!' : '💡 Explanation:'}
                    </p>
                    <p>{currentCard.quiz.explanation}</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Navigation hints */}
        <div className="absolute bottom-6 left-4 right-4 z-10">
          <div className="flex justify-between text-white text-opacity-60 text-xs">
            <span>{currentCardIndex > 0 ? '← Previous' : ''}</span>
            <span>{currentCardIndex + 1} of {totalCards}</span>
            <span>{currentCardIndex < totalCards - 1 ? 'Next →' : 'Close'}</span>
          </div>
        </div>

        {/* Invisible tap zones for better UX - Only when no quiz is active */}
        {!currentCard.quiz && (
          <>
            <div className="absolute left-0 top-0 w-1/2 h-full z-5" onClick={(e) => {
              e.stopPropagation();
              handlePrevious();
            }} />
            <div className="absolute right-0 top-0 w-1/2 h-full z-5" onClick={(e) => {
              e.stopPropagation();
              handleNext();
            }} />
          </>
        )}
      </div>
    </div>
  );
};

export default StoryViewer;